<template>
  <v-navigation-drawer
    color="white"
    data-test="nav-drawer"
    expand-on-hover
    permanent
    rail
    :width="width"
  >
    <v-list class="pt-0 px-0" density="compact" nav>
      <app-nav-item v-for="item in visibleTransactionItems" :key="item.label" v-bind="item" />
    </v-list>

    <v-divider class="mx-4" />

    <v-list class="px-0" density="compact" nav>
      <app-nav-item v-for="item in visibleMasterDataItems" :key="item.label" v-bind="item" />
    </v-list>

    <template v-slot:append>
      <v-list class="pb-8 px-0" density="compact" nav>
        <app-nav-item
          v-if="canManageCompany"
          data-test="nav-link-company"
          icon="mso:settings"
          label="Settings"
          :to="{ name: 'companies-id', params: { id: Number(userCompany?.id) } }"
        />
        <app-nav-item label="Profile" :to="{ name: 'profile' }">
          <template v-slot:icon>
            <user-avatar color="interactive" :user="user" />
          </template>
        </app-nav-item>
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import AppNavItem from '@/components/app-nav-item.vue'
import UserAvatar from '@/components/users/user-avatar.vue'

const REFRESH_INTERVAL = 5 * 60 * 1000 // 5 minutes

const accountingFailuresCount = ref(0)
const invoiceInboxCount = ref(0)
const showInvoicesItem = ref(false)
const width = ref(220)

const authStore = useAuthStore()
const canManageCompany = authStore.canManageCompany
const canManageInventory = authStore.canManageInventory
const canManageInvoices = authStore.canManageInvoices
const canManageUsers = authStore.canManageUsers
const canUseBom = authStore.canUseBom
const canUseExpenses = authStore.canUseExpenses
const canUseRequisitions = authStore.canUseRequisitions
const canViewInventory = authStore.canViewInventory
const inSetupMode = authStore.inSetupMode
const isStaff = authStore.isStaff
const userCompany = authStore.userCompany!
const user = authStore.user!

const showAccountingFailureLink = computed(() => isStaff || (!!userCompany?.accounting_integration && canManageCompany))
const transactionItems = computed(() => [
  {
    label: 'Home',
    to: { name: 'dashboard' },
    icon: 'mso:home',
    show: true,
    dataTest: 'nav-link-home',
  },
  {
    label: 'Orders',
    to: { name: 'orders' },
    icon: 'mso:package_2',
    show: !inSetupMode,
    dataTest: 'nav-link-orders',
  },
  {
    label: 'Expenses',
    to: { name: 'expenses' },
    icon: 'mso:receipt_long',
    show: !inSetupMode && canUseExpenses,
    dataTest: 'nax-link-expenses',
  },
  {
    label: 'Requisitions',
    to: { name: 'requisitions' },
    icon: 'mso:shopping_cart',
    show: !inSetupMode && (isStaff || canUseRequisitions),
    dataTest: 'nav-link-reqsuitions'
  },
  {
    label: 'Invoices',
    to: { name: 'invoices' },
    icon: 'mso:attach_money',
    show: !inSetupMode && showInvoicesItem.value,
    dataTest: 'nav-link-invoices',
    countBadge: invoiceInboxCount.value,
  },
  {
    label: 'Accounting Sync',
    to: { name: 'accounting' },
    icon: 'mso:sync',
    show: showAccountingFailureLink.value,
    dataTest: 'nav-link-accounting',
    countBadge: accountingFailuresCount.value,
  },
  {
    label: 'PO Export',
    to: { name: 'accounting-export' },
    icon: 'mso:file_export',
    dataTest: 'nav-link-accounting-export',
    show: isStaff || !!userCompany?.bulk_report_type,
  },
])
const masterDataItems = computed(() => [
  {
    label: 'Projects',
    to: { name: 'projects' },
    icon: 'mso:folder',
    show: true,
    dataTest: 'nav-link-projects',
  },
  {
    label: 'Bill of Materials',
    to: { name: 'boms' },
    icon: 'mso:list_alt',
    show: canUseBom,
    dataTest: 'nav-link-boms',
  },
  {
    label: 'Inventory',
    to: { name: 'inventory' },
    icon: 'mso:warehouse',
    show: canManageInventory || canViewInventory,
    dataTest: 'nav-link-inventory',
  },
  {
    label: 'Suppliers',
    to: { name: 'companies-id-suppliers', params: { id: userCompany.id } },
    icon: 'mso:store',
    show: true,
  },
  {
    label: 'Suppliers STAFF',
    to: { name: 'suppliers' },
    icon: 'mso:store',
    show: isStaff,
    dataTest: 'nav-link-suppliers',
  },
  {
    label: 'Users',
    to: { name: 'users' },
    icon: 'mso:group',
    show: canManageUsers,
    dataTest: 'nav-link-users',
  },
])
const visibleTransactionItems = computed(() => transactionItems.value.filter(item => item.show))
const visibleMasterDataItems = computed(() => masterDataItems.value.filter(item => item.show))

const { $api, $error } = useNuxtApp()
const refreshAccountingFailures = async () => {
  const failureCounts = await Promise.allSettled([
    $api.v1.purchaseOrders.list({ accounting_success: false, limit: 1 }).then(response => response.count),
    $api.v1.receipts.list({ accounting_success: false, limit: 1 }).then(response => response.count),
    $api.v1.expenses.getAccountingFailuresCount(),
    $api.v1.invoices.list({ accounting_success: false, limit: 1 }).then(response => response.count)
  ]).then(results => results.filter(r => r.status === 'fulfilled').map(r => r.value))

  accountingFailuresCount.value = failureCounts.reduce((acc, count) => acc + count, 0)
}
const refreshInvoiceCount = () => {
  $api.v1.attachments.list({ category: 4, company: userCompany.id, limit: 1, unassociated: true })
    .then(response => { invoiceInboxCount.value = response.count })
    .catch(console.error)
}
let refreshInvoiceCountTimer: ReturnType<typeof setInterval>
const setShowInvoices = () => {
  if (canManageInvoices) {
    showInvoicesItem.value = true
    refreshInvoiceCount()
    refreshInvoiceCountTimer = setInterval(refreshInvoiceCount, REFRESH_INTERVAL)
  }
  else {
    $api.v1.jobsites.list({ invoice_approver: user?.id, invoices: true, limit: 1 } as any)
      .then(response => { showInvoicesItem.value = response.data.length > 0 })
      .catch($error.report)
  }
}

let accountingFailuresTimer: ReturnType<typeof setInterval>
onMounted(() => {
  if (showAccountingFailureLink.value) {
    refreshAccountingFailures()
    accountingFailuresTimer = setInterval(refreshAccountingFailures, REFRESH_INTERVAL)
  }
  setShowInvoices()
})
onBeforeUnmount(() => {
  clearInterval(accountingFailuresTimer)
  clearInterval(refreshInvoiceCountTimer)
})
</script>
